import type { ReactNode } from 'react'
import type { StoredUsers } from '~/gql/users/users'
import type { UserSelection } from '~/pages/backoffice/admin/user/list/use-user-selection'
import type { SortedInfo } from '~/pages/backoffice/useTableQuery'
import { useTranslation } from 'react-i18next'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '~/components/ui/table'
import { tourAnchor } from '~/helpers/tour/anchors'
import UserRow from '~/pages/backoffice/admin/user/list/UserRow'
import SortableTableHead from '~/pages/backoffice/SortableTableHead'

type IProps = {
  users: StoredUsers[]
  currentUserId?: string | null
  sortedInfo?: SortedInfo
  onSort: (field: string) => void
  /** Cases d'affectation en masse ; sans elle, pas de colonne. */
  selection?: UserSelection
  renderActions: (user: StoredUsers) => ReactNode
}

/** Le tableau des comptes, trié côté serveur. */
function UsersList({ users, currentUserId, sortedInfo, onSort, selection, renderActions }: IProps) {
  const { t } = useTranslation()
  const pageIds = users.map(u => u.id)
  const sortable = (field: string, label: string) => (
    <SortableTableHead field={field} label={label} sortedInfo={sortedInfo} onSort={onSort} />
  )

  return (
    <Table aria-label={t('common.routing.users')}>
      <TableHeader>
        <TableRow>
          {selection && (
            <TableHead className="w-[40px]">
              <input
                type="checkbox"
                checked={selection.isPageSelected(pageIds)}
                onChange={() => selection.togglePage(pageIds)}
                aria-label={t('groups.bulk.selectPage')}
                {...tourAnchor('users.select')}
              />
            </TableHead>
          )}
          {sortable('email', t('common.email'))}
          {sortable('firstName', t('common.firstname'))}
          {sortable('lastName', t('common.lastname'))}
          <TableHead>{t('user.company')}</TableHead>
          <TableHead>{t('user.department')}</TableHead>
          {sortable('role', t('common.role'))}
          <TableHead>{t('common.groups')}</TableHead>
          {sortable('lastConnectionAt', t('common.lastConnection'))}
          {sortable('status', t('common.status'))}
          <TableHead className="w-[80px] text-right">{t('common.action')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map(user => (
          <UserRow
            key={user.id}
            user={user}
            isSelf={currentUserId === user.id}
            selection={selection && {
              checked: selection.selectedIds.has(user.id),
              onToggle: () => selection.toggleRow(user.id),
            }}
            actions={renderActions(user)}
          />
        ))}
      </TableBody>
    </Table>
  )
}

export default UsersList
